/**
 * Command Queue Implementation
 * 
 * Manages the queue of SDK method calls that occur before the remote
 * implementation script has loaded. Commands are stored as tuples
 * and processed FIFO when the remote script becomes available.
 */

import type { Command, EncatchSDK } from './types';

/**
 * Process all queued commands by calling the corresponding methods
 * on the SDK instance. This is called by the remote script once loaded.
 * 
 * @param sdk - The SDK instance with real implementations
 * @param queue - Array of queued commands to process
 */
export function processQueue(sdk: EncatchSDK, queue: Command[]): void {
  while (queue.length > 0) {
    const command = queue.shift();
    if (!command) continue;

    const [method, ...args] = command;
    
    // Skip internal properties
    if (method.startsWith('_')) continue;

    // Get the method from the SDK
    const fn = sdk[method as keyof EncatchSDK];
    
    if (typeof fn === 'function') {
      try {
        (fn as (...args: unknown[]) => void).apply(sdk, args);
      } catch (error) {
        console.error(`[Encatch] Error processing queued command "${method}":`, error);
      }
    } else {
      console.warn(`[Encatch] Unknown method in queue: "${method}"`);
    }
  }
}

/**
 * Creates a stub method that queues calls for later processing
 * 
 * @param queue - The command queue array
 * @param methodName - Name of the method being stubbed
 * @returns A function that pushes calls to the queue
 */
export function createQueuedMethod(
  queue: Command[],
  methodName: string
): (...args: unknown[]) => void {
  return function (...args: unknown[]): void {
    queue.push([methodName, ...args]);
  };
}

/**
 * List of SDK methods that should be queued before initialization
 */
export const QUEUED_METHODS = [
  'init',
  'identifyUser',
  'setLocale',
  'setCountry',
  'setTheme',
  'trackEvent',
  'trackScreen',
  'startSession',
  'pauseSession',
  'resumeSession',
  'stopSession',
  'resetUser',
  'showForm',
  'dismissForm',
  'addToResponse',
  'submitForm',
  'emitEvent',
  'clearAll',
  'addSourceTracking',
] as const;

export type QueuedMethodName = typeof QUEUED_METHODS[number];
